import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import '../styles/Auth.css';

const ForgotPassword = () => {
  const [step, setStep] = useState('email'); // 'email' or 'reset'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Glömt lösenord - TrafikBevakning';
    document.body.style.backgroundColor = '#f5f5f7';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email || !email.includes('@')) {
      setError('Giltig e-postadress krävs');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep('reset');
        setMessage('En återställningskod har skickats till din e-post.');
      } else {
        setError(data.error || 'Något gick fel');
      }
    } catch (error) {
      console.error('Request error:', error);
      setError('Kunde inte ansluta till servern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const otpString = otp.join('');
    if (otpString.length < 6) {
      setError('Ange hela verifieringskoden');
      return;
    }

    if (!newPassword) {
      setError('Nytt lösenord krävs');
      return;
    }

    // Password validation
    const hasLength = newPassword.length >= 12;
    const hasAlpha = /[a-zA-Z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSymbol = /[^a-zA-Z0-9]/.test(newPassword);

    if (!hasLength || !hasAlpha || !hasNumber || !hasSymbol) {
      setError('Lösenordet uppfyller inte kraven (Minst 12 tecken, 1 bokstav, 1 siffra, 1 symbol)');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          otp: otpString,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        navigate('/login', {
          state: {
            email: email,
            message: 'Lösenordet har återställts! Du kan nu logga in med ditt nya lösenord.',
          },
        });
      } else {
        setError(data.error || 'Återställning misslyckades');
      }
    } catch (error) {
      console.error('Reset error:', error);
      setError('Kunde inte ansluta till servern');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP Input Handlers (Same as Register.js)
  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;
    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);
    if (element.value !== '') {
      if (index < 5) document.getElementById(`otp-input-${index + 1}`).focus();
    }
  };

  const handleBackspace = (e, index) => {
    if (e.key === 'Backspace') {
      if (index > 0 && otp[index] === '') {
        document.getElementById(`otp-input-${index - 1}`).focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const data = e.clipboardData.getData('text').trim();
    if (!/^\d+$/.test(data)) return;
    const newOtp = [...otp];
    for (let i = 0; i < Math.min(data.length, 6); i++) {
      newOtp[i] = data[i];
    }
    setOtp(newOtp);
    const nextIndex = Math.min(data.length, 5);
    document.getElementById(`otp-input-${nextIndex}`)?.focus();
  };

  const passwordRequirements = [
    { label: 'Minst 12 tecken', valid: newPassword.length >= 12 },
    { label: 'Minst 1 bokstav', valid: /[a-zA-Z]/.test(newPassword) },
    { label: 'Minst 1 siffra', valid: /[0-9]/.test(newPassword) },
    { label: 'Minst 1 symbol', valid: /[^a-zA-Z0-9]/.test(newPassword) },
  ];

  return (
    <div className="auth-wrapper notranslate" translate="no">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-title">Återställ lösenord</h1>
          <p className="auth-subtitle">
            {step === 'email'
              ? 'Ange din e-postadress för att återställa ditt lösenord'
              : `Ange koden som skickades till ${email}`}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {message && !error && (
          <div
            style={{
              backgroundColor: '#d4edda',
              color: '#155724',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '15px',
              textAlign: 'center',
            }}
          >
            {message}
          </div>
        )}

        {step === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">E-postadress</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din.email@example.com"
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={isLoading}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Skickar...
                </>
              ) : (
                'Skicka återställningskod'
              )}
            </button>

            <div className="auth-links">
              <p>
                <Link to="/login">Tillbaka till inloggning</Link>
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetSubmit} className="auth-form">
            <div className="form-group" style={{ textAlign: 'center' }}>
              <label style={{ marginBottom: '15px' }}>Verifieringskod (6 siffror)</label>
              <div className="otp-container notranslate">
                {otp.map((data, index) => (
                  <input
                    className="otp-input"
                    type="text"
                    name="otp"
                    maxLength="1"
                    key={index}
                    id={`otp-input-${index}`}
                    value={data}
                    onChange={(e) => handleChange(e.target, index)}
                    onKeyDown={(e) => handleBackspace(e, index)}
                    onPaste={handlePaste}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>
            </div>

            <div className="form-group">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <label htmlFor="newPassword">Nytt lösenord</label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    color: '#666',
                    cursor: 'pointer',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  title={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minst 12 tecken"
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <div
              className="password-requirements"
              style={{
                fontSize: '0.85rem',
                color: '#666',
                marginTop: '-10px',
                marginBottom: '15px',
                paddingLeft: '5px',
              }}
            >
              <p style={{ marginBottom: '5px', fontWeight: '500' }}>Lösenordet måste innehålla:</p>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '5px',
                }}
              >
                {passwordRequirements.map((req, index) => (
                  <li
                    key={index}
                    style={{
                      color: req.valid ? 'green' : '#666',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ marginRight: '6px', fontSize: '1.1em' }}>
                      {req.valid ? '✓' : '•'}
                    </span>
                    {req.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Bekräfta nytt lösenord</label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Upprepa lösenordet"
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={isLoading}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Återställer...
                </>
              ) : (
                'Återställ Lösenord'
              )}
            </button>

            <div className="auth-links">
              <button
                type="button"
                onClick={() => setStep('email')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Ändra e-postadress
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
